import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { InductionRecord, MandatoryTrainingRecord, MandatorySettings } from '../types';
import { getQuestions, getPostTestQuestions, getMandatorySettings } from './storageService';

export const createMandatoryTrainingPDFDoc = async (record: MandatoryTrainingRecord) => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  let settings: MandatorySettings | null = null;
  try {
    settings = await getMandatorySettings();
  } catch (e) {
    console.error('Failed to load mandatory settings for certificate:', e);
  }
  const instructorName = settings?.instructorName || 'Tim EHS Training PT United Tractors Tbk';
  const ehsManagerName = settings?.ehsManagerName || 'EHS Department Head';

  const generateQR = async (text: string): Promise<string> => {
    try {
      return await QRCode.toDataURL(text, { width: 100, margin: 1 });
    } catch (err) {
      console.error(err);
      return '';
    }
  };

  const qrData = await generateQR(`Mandatory Safety Training\nParticipant: ${record.fullName}\nNIK: ${record.nik}\nCompany: ${record.company}\nModule: ${record.moduleTitle}\nScore: ${record.score}\nDate: ${record.completionDate}`);

  // Outer border
  doc.setDrawColor(30, 80, 150);
  doc.setLineWidth(1.2);
  doc.rect(8, 8, 194, 281, 'S');

  doc.setDrawColor(230, 160, 20);
  doc.setLineWidth(0.4);
  doc.rect(9.5, 9.5, 191, 278, 'S');

  // Header band
  doc.setFillColor(30, 80, 150);
  doc.rect(8, 8, 194, 28, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('MANDATORY SAFETY TRAINING CERTIFICATE', 105, 18, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('PT UNITED TRACTORS TBK  •  EHS MARKETING DIVISION', 105, 25, { align: 'center' });

  // Body
  doc.setTextColor(70, 80, 90);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(10.5);
  doc.text('Dengan ini menerangkan bahwa / This is to certify that:', 105, 52, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(20, 70, 130);
  doc.text(record.fullName.toUpperCase(), 105, 68, { align: 'center' });

  doc.setDrawColor(20, 70, 130);
  doc.setLineWidth(0.6);
  doc.line(40, 72, 170, 72);

  doc.setTextColor(80, 90, 100);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Telah menyelesaikan Modul Pembelajaran & Evaluasi Keselamatan Kerja Wajib:', 105, 84, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(220, 100, 0);
  doc.text(record.moduleTitle.toUpperCase(), 105, 93, { align: 'center' });

  // Metadata Box
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(210, 220, 230);
  doc.setLineWidth(0.4);
  doc.rect(20, 103, 170, 85, 'FD');

  const cardYStart = 111;
  const rowHeight = 18;

  const drawItem = (labelId: string, labelEn: string, value: string, x: number, y: number) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(110, 120, 130);
    doc.text(`${labelId.toUpperCase()} / ${labelEn.toUpperCase()}`, x, y);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(30, 40, 50);
    const wrapped = doc.splitTextToSize(value, 70);
    doc.text(wrapped, x, y + 5.5);
  };

  drawItem('NRP / NIK', 'Employee NRP/ID', record.nik || '-', 28, cardYStart);
  drawItem('Posisi / Jabatan', 'Position', record.position || '-', 28, cardYStart + rowHeight);
  drawItem('Perusahaan / Dept', 'Company / Dept', record.company || '-', 28, cardYStart + rowHeight * 2);
  drawItem('Area Kerja', 'Work Area', record.workArea || 'Yard Sukapura', 28, cardYStart + rowHeight * 3);

  drawItem('Modul Training', 'Training Module', record.moduleTitle, 110, cardYStart);
  drawItem('Tanggal Selesai', 'Completion Date', record.completionDate, 110, cardYStart + rowHeight);
  drawItem('Skor Evaluasi', 'Post-Test Score', `${record.score} / 100`, 110, cardYStart + rowHeight * 2);
  drawItem('Status Kelulusan', 'Status', record.passed ? 'LULUS / PASSED' : 'BELUM LULUS', 110, cardYStart + rowHeight * 3);

  // Signatures Block
  const sigY = 200;
  doc.setDrawColor(220, 225, 230);
  doc.setLineWidth(0.3);
  doc.line(25, sigY - 14, 185, sigY - 14);

  // Left Signature: Pemateri / Instructor
  if (qrData) {
    doc.addImage(qrData, 'PNG', 44, sigY - 5, 24, 24);
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(50, 55, 60);
  const wrappedInstructor = doc.splitTextToSize(instructorName, 70);
  doc.text(wrappedInstructor, 56, sigY + 22, { align: 'center' });
  doc.setDrawColor(180, 185, 190);
  doc.setLineWidth(0.2);
  doc.line(25, sigY + 24, 87, sigY + 24);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(130, 140, 150);
  doc.text('(Pemberi Materi / Instructor)', 56, sigY + 28, { align: 'center' });

  // Right Signature: EHS Manager
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(50, 55, 60);
  const wrappedManager = doc.splitTextToSize(ehsManagerName, 70);
  doc.text(wrappedManager, 154, sigY + 22, { align: 'center' });
  doc.setDrawColor(180, 185, 190);
  doc.setLineWidth(0.2);
  doc.line(123, sigY + 24, 185, sigY + 24);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(130, 140, 150);
  doc.text('(Head of EHS Department)', 154, sigY + 28, { align: 'center' });

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7.5);
  doc.setTextColor(150, 155, 160);
  doc.text('Verifikasi Digital Sertifikat Safety Mandatory Training', 105, sigY + 38, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(150, 155, 160);
  doc.text(`ID Transaksi: ${record.id}  •  Aplikasi By IRL92`, 105, 275, { align: 'center' });

  return doc;
};

export const generateMandatoryTrainingPDF = async (record: MandatoryTrainingRecord) => {
  const doc = await createMandatoryTrainingPDFDoc(record);
  doc.save(`MandatoryTraining_${record.moduleTitle.replace(/\s+/g, '_')}_${record.fullName.replace(/\s+/g, '_')}.pdf`);
};

export const getMandatoryTrainingPDFDataUri = async (record: MandatoryTrainingRecord): Promise<string> => {
  const doc = await createMandatoryTrainingPDFDoc(record);
  return doc.output('datauristring');
};

export const createInductionPDFDoc = async (record: InductionRecord) => {
  const [questions, postTestQuestions] = await Promise.all([
    getQuestions(),
    getPostTestQuestions()
  ]);

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  // Helper to generate QR Data URL
  const generateQR = async (text: string): Promise<string> => {
    try {
      return await QRCode.toDataURL(text, { width: 100, margin: 1 });
    } catch (err) {
      console.error(err);
      return '';
    }
  };

  const qrParticipant = await generateQR(`Participant: ${record.fullName}\nJob Title: ${record.jobTitle}\nCompany: ${record.company}\nDate: ${record.inductionDate}`);
  const qrInstructor = await generateQR(`Instructor: ${record.instructor}\nApproved Safety Induction`);

  // Calculate Expiry Date (1 Year)
  const inductionDateObj = new Date(record.inductionDate);
  const expiryDate = new Date(inductionDateObj);
  expiryDate.setFullYear(expiryDate.getFullYear() + 1);
  const formattedExpiry = expiryDate.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
  const formattedInduction = inductionDateObj.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });

  // ==========================================
  // PAGE 1: PROFESSIONAL CERTIFICATE
  // ==========================================
  
  // 1. Decorative border
  doc.setDrawColor(41, 128, 185); // Professional Blue
  doc.setLineWidth(1.0);
  doc.rect(8, 8, 194, 281, 'S'); // Outer thick border
  
  doc.setDrawColor(241, 196, 15); // Accent Yellow
  doc.setLineWidth(0.3);
  doc.rect(9.5, 9.5, 191, 278, 'S'); // Inner thin border

  // 2. Premium Certificate Header Band
  doc.setFillColor(41, 128, 185);
  doc.rect(8, 8, 194, 25, 'F');

  // Header Text
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text('SAFETY INDUCTION CERTIFICATE', 105, 17, { align: 'center' });
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text('PT UNITED TRACTORS TBK  •  MARKETING DIVISION', 105, 23, { align: 'center' });

  // 3. Certificate Body Text
  doc.setTextColor(70, 80, 90);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(10);
  doc.text('This is to certify that / Dengan ini menerangkan bahwa:', 105, 48, { align: 'center' });

  // Participant Name (Big & Bold)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(33, 97, 140); // Dark Blue Accent
  doc.text(record.fullName.toUpperCase(), 105, 62, { align: 'center' });

  // Underlying name accent decoration
  doc.setDrawColor(33, 97, 140);
  doc.setLineWidth(0.6);
  doc.line(45, 66, 165, 66);

  // Statement text
  doc.setTextColor(80, 90, 100);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.text('Has successfully completed the Safety Induction program and met all evaluation standards for:', 105, 76, { align: 'center' });
  doc.setFont('helvetica', 'italic');
  doc.text('Telah berhasil menyelesaikan program Induksi Keselamatan dan memenuhi seluruh standar evaluasi untuk:', 105, 81, { align: 'center' });

  // 4. Metadata Card in the Middle
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(220, 225, 230);
  doc.setLineWidth(0.35);
  doc.rect(18, 90, 174, 68, 'FD');

  const cardYStart = 96;
  const rowHeight = 17;

  // Render Metadata Helper
  const drawCardItem = (labelId: string, labelEn: string, value: string, x: number, y: number) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(110, 120, 130);
    doc.text(`${labelId.toUpperCase()} / ${labelEn.toUpperCase()}`, x, y);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(40, 50, 60);
    const wrappedVal = doc.splitTextToSize(value, 75);
    doc.text(wrappedVal, x, y + 5.5);
  };

  drawCardItem('Perusahaan', 'Company', record.company || '-', 24, cardYStart);
  drawCardItem('Jabatan', 'Job Title', record.jobTitle || '-', 24, cardYStart + rowHeight);
  drawCardItem('Area Kerja', 'Work Area', record.area, 24, cardYStart + rowHeight * 2);

  drawCardItem('Tanggal Induksi', 'Induction Date', formattedInduction, 112, cardYStart);
  drawCardItem('Berlaku S/D', 'Valid Until', formattedExpiry, 112, cardYStart + rowHeight);
  
  const postTestText = record.visitorType === 'TAMU'
    ? 'Dilewati (Tamu) / Skipped (Guest)'
    : record.postTestScore !== undefined
      ? `${record.postTestScore} / 100 (${record.postTestPassed ? 'LULUS / PASSED' : 'GAGAL / FAILED'})`
      : 'TIDAK ADA / NONE';
  drawCardItem('Nilai Post-Test', 'Post-Test Score', postTestText, 112, cardYStart + rowHeight * 2);

  // 5. Signatures Block (Positioned perfectly at the bottom to avoid overcrowding)
  const sigY = 178;
  doc.setDrawColor(220, 225, 230);
  doc.setLineWidth(0.3);
  doc.line(25, 171, 185, 171); // elegant separator line

  // Left Signature: Participant
  if (qrParticipant) {
    doc.addImage(qrParticipant, 'PNG', 44, sigY, 24, 24);
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(50, 55, 60);
  doc.text(record.fullName, 56, sigY + 31, { align: 'center' });
  doc.setDrawColor(180, 185, 190);
  doc.setLineWidth(0.2);
  doc.line(25, sigY + 33, 87, sigY + 33);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(130, 140, 150);
  doc.text('(Participant Signature / Tanda Tangan Peserta)', 56, sigY + 37, { align: 'center' });

  // Right Signature: Instructor
  if (qrInstructor) {
    doc.addImage(qrInstructor, 'PNG', 142, sigY, 24, 24);
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(50, 55, 60);
  doc.text(record.instructor, 154, sigY + 31, { align: 'center' });
  doc.setDrawColor(180, 185, 190);
  doc.setLineWidth(0.2);
  doc.line(123, sigY + 33, 185, sigY + 33);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(130, 140, 150);
  doc.text('(ESR Officer / Pemberi Materi)', 154, sigY + 37, { align: 'center' });

  // 6. Bottom Certificate Notice
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7.5);
  doc.setTextColor(150, 155, 160);
  doc.text('This digital certificate is a valid proof of PT United Tractors Tbk Safety Induction.', 105, 242, { align: 'center' });
  doc.text('Sertifikat digital ini merupakan bukti sah pelaksanaan Safety Induction PT United Tractors Tbk.', 105, 246, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(160, 165, 170);
  doc.text(`ID Transaksi / Record hash: ${record.id}  •  Ditetapkan oleh ESR Committee PT United Tractors Tbk`, 105, 258, { align: 'center' });
  doc.text('Aplikasi By IRL92', 105, 263, { align: 'center' });


  // ==========================================
  // PAGE 2+: EVALUATION & TESTING DETAILS
  // ==========================================
  doc.addPage();
  
  // Outer Borders for Page 2
  doc.setDrawColor(41, 128, 185);
  doc.setLineWidth(1.0);
  doc.rect(8, 8, 194, 281, 'S');
  doc.setDrawColor(241, 196, 15);
  doc.setLineWidth(0.3);
  doc.rect(9.5, 9.5, 191, 278, 'S');

  // Title for Transcript Page
  doc.setTextColor(41, 128, 185);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('TRANSKRIP EVALUASI & PENILAIAN SAFETY INDUCTION', 105, 20, { align: 'center' });
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 110, 120);
  doc.text('SAFETY INDUCTION ASSESSMENT & EVALUATION TRANSCRIPT', 105, 24, { align: 'center' });

  doc.setDrawColor(220, 225, 230);
  doc.setLineWidth(0.25);
  doc.line(15, 28, 195, 28);

  let currentY = 35;

  // Helper function to dynamically add pages when Y overflows (safely avoiding bottom border at 281mm)
  const checkPageOverflow = (heightNeeded: number) => {
    if (currentY + heightNeeded > 268) {
      doc.addPage();
      
      // Draw outer borders for the new continuation page
      doc.setDrawColor(41, 128, 185);
      doc.setLineWidth(1.0);
      doc.rect(8, 8, 194, 281, 'S');
      doc.setDrawColor(241, 196, 15);
      doc.setLineWidth(0.3);
      doc.rect(9.5, 9.5, 191, 278, 'S');
      
      // Top header of continuation page
      doc.setTextColor(110, 120, 130);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.text('Safety Induction Transcript - Continued / Lanjutan', 15, 16);
      doc.setDrawColor(220, 225, 230);
      doc.setLineWidth(0.2);
      doc.line(15, 18, 195, 18);
      
      currentY = 24; // Start right below the continuation line
    }
  };

  // --- SECTION I: GENERAL COMPREHENSION ---
  checkPageOverflow(12);
  doc.setTextColor(41, 128, 185);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('I. CHECKLIST PEMAHAMAN UMUM / GENERAL COMPREHENSION CHECKLIST', 15, currentY);
  currentY += 6;

  questions.forEach((q, index) => {
    const answer = record.answers[q.id];
    let answerText = 'TIDAK / NO';
    let isYes = false;
    if (answer === true) {
      answerText = 'YA / YES';
      isYes = true;
    }

    const textLines = doc.splitTextToSize(`${index + 1}. ${q.text}`, 145);
    const textEnLines = q.textEn ? doc.splitTextToSize(q.textEn, 145) : [];
    
    const blockHeight = (textLines.length * 3.5) + (textEnLines.length * 3) + 4;
    checkPageOverflow(blockHeight);

    // Question body
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(50, 55, 60);
    doc.text(textLines, 15, currentY);
    currentY += textLines.length * 3.5;

    if (q.textEn) {
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(120, 125, 130);
      doc.text(textEnLines, 15, currentY);
      currentY += textEnLines.length * 3;
    }

    // Answer Status right-aligned block at X = 168
    if (isYes) {
      doc.setTextColor(46, 125, 50); // green
    } else {
      doc.setTextColor(198, 40, 40); // red
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    // Align with question baseline
    doc.text(answerText, 172, currentY - (textLines.length * 1.5) - 1, { align: 'left' });

    currentY += 1.5;
    doc.setDrawColor(240, 242, 245);
    doc.setLineWidth(0.15);
    doc.line(15, currentY, 195, currentY);
    currentY += 3.5;
  });

  // --- SECTION II: POST-TEST DETAIL LISTING ---
  if (record.postTestAnswers) {
    currentY += 4;
    checkPageOverflow(20);

    doc.setTextColor(41, 128, 185);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('II. DETAIL EVALUASI POST-TEST / SAFETY ASSESSMENT RESULTS', 15, currentY);
    currentY += 6;

    // Score Display Box
    const passed = record.postTestPassed;
    if (passed) {
      doc.setFillColor(230, 245, 233); // elegant soft green
      doc.setDrawColor(46, 125, 50);
    } else {
      doc.setFillColor(254, 237, 238); // elegant soft red
      doc.setDrawColor(211, 47, 47);
    }
    doc.setLineWidth(0.3);
    doc.rect(15, currentY, 180, 11, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    if (passed) {
      doc.setTextColor(27, 94, 32);
      doc.text(`STATUS KELULUSAN / EVALUATION: LULUS (PASSED)   •   NILAI AKHIR / SCORE: ${record.postTestScore} / 100`, 20, currentY + 7);
    } else {
      doc.setTextColor(198, 40, 40);
      doc.text(`STATUS KELULUSAN / EVALUATION: BELUM LULUS (FAILED)   •   NILAI AKHIR / SCORE: ${record.postTestScore} / 100`, 20, currentY + 7);
    }
    currentY += 17;

    // Questions list
    postTestQuestions.forEach((q, index) => {
      const answerIdx = record.postTestAnswers ? record.postTestAnswers[q.id] : undefined;
      const isCorrect = answerIdx === q.correctOptionIndex;

      const textLines = doc.splitTextToSize(`${index + 1}. ${q.text}`, 172);
      const textEnLines = q.textEn ? doc.splitTextToSize(q.textEn, 172) : [];
      
      let userAnsLabel = '-';
      if (answerIdx !== undefined) {
        const optionLetter = String.fromCharCode(65 + answerIdx);
        const indOption = q.options[answerIdx];
        const enOptionStr = q.optionsEn?.[answerIdx];
        userAnsLabel = `${optionLetter}. ${indOption}${enOptionStr ? ` (${enOptionStr})` : ''}`;
      }
      
      const ansStr = `Jawaban Anda / Your Choice: ${userAnsLabel}`;
      const ansLines = doc.splitTextToSize(ansStr, 168);

      let correctLines: string[] = [];
      if (!isCorrect) {
        const correctLetter = String.fromCharCode(65 + q.correctOptionIndex);
        const indCorrect = q.options[q.correctOptionIndex];
        const enCorrect = q.optionsEn?.[q.correctOptionIndex];
        const correctAnsLabel = `Kunci Jawaban / Correct Key: ${correctLetter}. ${indCorrect}${enCorrect ? ` (${enCorrect})` : ''}`;
        correctLines = doc.splitTextToSize(correctAnsLabel, 168);
      }

      const postTestBlockHeight = (textLines.length * 3.5) + 
                                  (textEnLines.length * 3) + 
                                  (ansLines.length * 3.2) + 
                                  (correctLines.length * 3.2) + 
                                  10;

      checkPageOverflow(postTestBlockHeight);

      // Question Number & Text
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(50, 55, 60);
      doc.text(textLines, 15, currentY);
      currentY += textLines.length * 3.5;

      // English question
      if (q.textEn) {
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(120, 125, 130);
        doc.text(textEnLines, 15, currentY);
        currentY += textEnLines.length * 3;
      }

      currentY += 1.5;

      // Draw Selected Choice
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.2);
      if (isCorrect) {
        doc.setTextColor(46, 125, 50); // green
      } else {
        doc.setTextColor(198, 40, 40); // red
      }
      doc.text(ansLines, 18, currentY);
      currentY += ansLines.length * 3.2;

      // Draw correct answer key if wrong
      if (!isCorrect) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(41, 128, 185); // blue
        doc.text(correctLines, 18, currentY);
        currentY += correctLines.length * 3.2;
      }

      currentY += 2;
      doc.setDrawColor(240, 242, 245);
      doc.setLineWidth(0.15);
      doc.line(15, currentY, 195, currentY);
      currentY += 4.5;
    });
  } else {
    currentY += 4;
    checkPageOverflow(12);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(130, 135, 140);
    const notTakenMsg = record.visitorType === 'TAMU'
      ? 'Post-Test dilewati (Status: Kunjungan Tamu) / Post-Test skipped (Status: Guest Visit).'
      : 'Post-Test tidak dilaksanakan / Post-test was not taken.';
    doc.text(notTakenMsg, 15, currentY);
  }

  // ==========================================
  // DYNAMIC PAGE NUMBERS AND WATERMARKS LOOP
  // ==========================================
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(6.5);
    doc.setTextColor(150, 155, 160);
    doc.setFont('helvetica', 'normal');
    
    if (i === 1) {
      // Page 1 is the high-fidelity certificate; has its own beautiful footer layout
      continue;
    }

    // Draw standard, neat footer details for Page 2 and subsequent pages
    doc.text(`ID Transaksi / Record hash: ${record.id}`, 15, 287);
    doc.text(`Halaman / Page ${i} dari / of ${totalPages}`, 195, 287, { align: 'right' });
    doc.text('Aplikasi By IRL92', 105, 287, { align: 'center' });
  }

  return doc;
};

export const generateInductionPDF = async (record: InductionRecord) => {
  const doc = await createInductionPDFDoc(record);
  doc.save(`SafetyInduction_${record.fullName.replace(/\s+/g, '_')}.pdf`);
};

export const getInductionPDFDataUri = async (record: InductionRecord): Promise<string> => {
  const doc = await createInductionPDFDoc(record);
  return doc.output('datauristring');
};

export interface MandatoryPersonMatrixItem {
  key: string;
  fullName: string;
  nik: string;
  company: string;
  position: string;
  workArea: string;
  passedCount: number;
  failedCount: number;
  unpassedCount: number;
  hasFailedRecord: boolean;
  avgScore: number;
  percentComplete: number;
  modulesMap: Record<string, { passed: boolean; score: number; completionDate: string }>;
}

export interface MandatoryMatrixExportOptions {
  personList: MandatoryPersonMatrixItem[];
  modules: Array<{ id: string; title: string; badge: string }>;
  selectedArea?: string;
  selectedStatus?: string;
  searchQuery?: string;
}

export const generateMTMatrixPDF = async (options: MandatoryMatrixExportOptions) => {
  const { personList, modules, selectedArea = 'all', selectedStatus = 'all', searchQuery = '' } = options;

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = 297;
  const marginX = 10;
  const contentWidth = pageWidth - (marginX * 2); // 277mm

  // Summary counts
  const totalPeserta = personList.length;
  const perfectCount = personList.filter(p => p.passedCount === modules.length).length;
  const inProgressCount = personList.filter(p => p.passedCount > 0 && p.passedCount < modules.length).length;
  const unpassedCount = personList.filter(p => p.passedCount === 0).length;
  const failedRecordCount = personList.filter(p => p.hasFailedRecord).length;

  const areaLabel = selectedArea === 'all' ? 'Semua Area Kerja' : selectedArea;
  const statusLabel = selectedStatus === 'perfect'
    ? 'Lulus Sempurna (7/7)'
    : selectedStatus === 'progress'
    ? 'Dalam Proses (1-6 Modul)'
    : selectedStatus === 'unpassed'
    ? 'Belum Lulus (0 Modul)'
    : selectedStatus === 'failed'
    ? 'Tercatat Tidak Lulus'
    : 'Semua Status';

  const dateNow = new Date();
  const dateStr = dateNow.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  // Table Column definitions (Sum = 277mm)
  const colNoW = 8;
  const colNameW = 45;
  const colPosW = 46;
  const colProgW = 42;
  const colModsTotalW = 136;

  const numMods = modules.length;
  const modWidths = [19, 20, 19, 20, 19, 20, 19]; // sum = 136
  const getModWidth = (idx: number) => {
    if (numMods === 7 && idx < modWidths.length) return modWidths[idx];
    return colModsTotalW / numMods;
  };

  const drawPageHeader = (pageNumber: number) => {
    if (pageNumber === 1) {
      // Primary header band
      doc.setFillColor(24, 43, 73); // deep navy
      doc.rect(marginX, 7, contentWidth, 16.5, 'F');

      // Accent gold strip
      doc.setFillColor(230, 160, 20); // amber gold
      doc.rect(marginX, 23.5, contentWidth, 1.2, 'F');

      // Text Header
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12.5);
      doc.text('MATRIKS PENCAPAIAN DETAIL 7 SAFETY MANDATORY', pageWidth / 2, 13.5, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(220, 230, 245);
      doc.text('SAFETY MANDATORY TRAINING  •  PT UNITED TRACTORS TBK  •  EHS MARKETING DIVISION', pageWidth / 2, 19.5, { align: 'center' });

      // Summary & Metadata Card
      const cardY = 27;
      const cardH = 10.5;
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(203, 213, 225);
      doc.setLineWidth(0.3);
      doc.roundedRect(marginX, cardY, contentWidth, cardH, 1.5, 1.5, 'FD');

      // Left info
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(30, 41, 59);
      doc.text(`AREA KERJA: ${areaLabel.toUpperCase()}`, marginX + 3.5, cardY + 4.2);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.8);
      doc.setTextColor(100, 116, 139);
      const searchInfo = searchQuery ? `  •  Cari: "${searchQuery}"` : '';
      doc.text(`Dicetak: ${dateStr}  •  Filter Status: ${statusLabel}${searchInfo}`, marginX + 3.5, cardY + 8);

      // Right KPI Chips
      let chipX = marginX + contentWidth - 3;

      const drawChip = (label: string, color: [number, number, number], bg: [number, number, number], border: [number, number, number]) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.5);
        const textWidth = doc.getTextWidth(label);
        const chipW = textWidth + 6;
        chipX -= chipW;

        doc.setFillColor(bg[0], bg[1], bg[2]);
        doc.setDrawColor(border[0], border[1], border[2]);
        doc.roundedRect(chipX, cardY + 2.2, chipW, 6.2, 1, 1, 'FD');

        doc.setTextColor(color[0], color[1], color[2]);
        doc.text(label, chipX + 3, cardY + 6.2);
        chipX -= 2.5; // space between chips
      };

      if (failedRecordCount > 0) {
        drawChip(`${failedRecordCount} Catatan Tidak Lulus`, [185, 28, 28], [254, 242, 242], [252, 165, 165]);
      }
      drawChip(`${unpassedCount} Belum Lulus (0/${modules.length})`, [220, 38, 38], [254, 242, 242], [252, 165, 165]);
      drawChip(`${inProgressCount} Dalam Proses`, [180, 83, 9], [254, 243, 199], [253, 230, 138]);
      drawChip(`${perfectCount} Lulus Sempurna (${modules.length}/${modules.length})`, [4, 120, 87], [236, 253, 245], [167, 243, 208]);
      drawChip(`${totalPeserta} Peserta`, [51, 65, 85], [241, 245, 249], [226, 232, 240]);
    } else {
      // Subsequent page compact header
      doc.setFillColor(24, 43, 73);
      doc.rect(marginX, 6, contentWidth, 6, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.text(`MATRIKS PENCAPAIAN DETAIL 7 SAFETY MANDATORY  •  AREA: ${areaLabel.toUpperCase()}  •  HALAMAN ${pageNumber}`, pageWidth / 2, 10.2, { align: 'center' });
    }
  };

  const drawTableHeader = (startY: number) => {
    const thH = 9.5;
    doc.setFillColor(241, 245, 249);
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.3);
    doc.rect(marginX, startY, contentWidth, thH, 'FD');

    // Vertical column separator lines in header
    doc.setDrawColor(203, 213, 225);
    let curX = marginX;

    // Col No
    doc.line(curX + colNoW, startY, curX + colNoW, startY + thH);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.8);
    doc.setTextColor(51, 65, 85);
    doc.text('NO', curX + colNoW / 2, startY + 6, { align: 'center' });
    curX += colNoW;

    // Col Name
    doc.line(curX + colNameW, startY, curX + colNameW, startY + thH);
    doc.text('NAMA & NRP / NIK', curX + 3, startY + 6);
    curX += colNameW;

    // Col Pos & Area
    doc.line(curX + colPosW, startY, curX + colPosW, startY + thH);
    doc.text('POSISI / PERUSAHAAN / AREA', curX + 3, startY + 6);
    curX += colPosW;

    // Col Progress
    doc.line(curX + colProgW, startY, curX + colProgW, startY + thH);
    doc.text('PROGRESS LULUS', curX + colProgW / 2, startY + 6, { align: 'center' });
    curX += colProgW;

    // Modules header: top banner spanning 7 modules
    const modTopH = 4.2;
    doc.setFillColor(235, 240, 246);
    doc.rect(curX, startY, colModsTotalW, modTopH, 'F');
    doc.line(curX, startY + modTopH, curX + colModsTotalW, startY + modTopH);
    doc.setFontSize(6.5);
    doc.setTextColor(30, 41, 59);
    doc.text(`STATUS ${modules.length} MODUL KESELAMATAN WAJIB`, curX + colModsTotalW / 2, startY + 3, { align: 'center' });

    // Sub-columns for each module badge
    let subX = curX;
    modules.forEach((mod, idx) => {
      const mw = getModWidth(idx);
      if (idx > 0) {
        doc.line(subX, startY + modTopH, subX, startY + thH);
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.2);
      doc.setTextColor(51, 65, 85);
      doc.text(mod.badge, subX + mw / 2, startY + modTopH + 3.8, { align: 'center' });
      subX += mw;
    });

    return startY + thH;
  };

  const drawVectorCheckmark = (cx: number, cy: number, color: [number, number, number]) => {
    doc.setDrawColor(color[0], color[1], color[2]);
    doc.setLineWidth(0.4);
    doc.line(cx - 1.6, cy - 0.1, cx - 0.4, cy + 1.1);
    doc.line(cx - 0.4, cy + 1.1, cx + 1.8, cy - 1.3);
  };

  const drawVectorCross = (cx: number, cy: number, color: [number, number, number]) => {
    doc.setDrawColor(color[0], color[1], color[2]);
    doc.setLineWidth(0.4);
    doc.line(cx - 1.2, cy - 1.2, cx + 1.2, cy + 1.2);
    doc.line(cx - 1.2, cy + 1.2, cx + 1.2, -1.2 + cy + 2.4);
    doc.line(cx - 1.2, cy + 1.2, cx + 1.2, cy - 1.2);
  };

  let currentPage = 1;
  drawPageHeader(currentPage);
  let currentY = drawTableHeader(39);

  const rowHeight = 10.8;
  const maxY = 196; // keep margin for footer at 203

  if (personList.length === 0) {
    doc.setFillColor(255, 255, 255);
    doc.rect(marginX, currentY, contentWidth, 20, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.rect(marginX, currentY, contentWidth, 20, 'S');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(148, 163, 184);
    doc.text('Tidak ada data peserta yang sesuai dengan pencarian atau filter area.', pageWidth / 2, currentY + 11, { align: 'center' });
  } else {
    personList.forEach((person, index) => {
      // Check page break
      if (currentY + rowHeight > maxY) {
        doc.addPage();
        currentPage++;
        drawPageHeader(currentPage);
        currentY = drawTableHeader(14);
      }

      const isEven = index % 2 === 0;
      doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 252);
      doc.rect(marginX, currentY, contentWidth, rowHeight, 'F');

      // Outer border & grid line
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.2);
      doc.line(marginX, currentY + rowHeight, marginX + contentWidth, currentY + rowHeight);

      // Col 1: No
      let colX = marginX;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.8);
      doc.setTextColor(100, 116, 139);
      doc.text(String(index + 1), colX + colNoW / 2, currentY + 6.4, { align: 'center' });
      colX += colNoW;

      // Col 2: Nama & NRP
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.2);
      doc.setTextColor(15, 23, 42);
      const cleanName = person.fullName.length > 25 ? person.fullName.substring(0, 24) + '...' : person.fullName;
      doc.text(cleanName, colX + 2.5, currentY + 4.2);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.0);
      doc.setTextColor(100, 116, 139);
      doc.text(`NRP: ${person.nik || '-'}`, colX + 2.5, currentY + 8.2);
      colX += colNameW;

      // Col 3: Posisi / Area
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.8);
      doc.setTextColor(30, 41, 59);
      const cleanPos = person.position.length > 26 ? person.position.substring(0, 25) + '...' : person.position;
      doc.text(cleanPos || '-', colX + 2.5, currentY + 4.2);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.0);
      doc.setTextColor(100, 116, 139);
      const companyArea = `${person.company || '-'} • ${person.workArea || '-'}`;
      const cleanComp = companyArea.length > 28 ? companyArea.substring(0, 27) + '...' : companyArea;
      doc.text(cleanComp, colX + 2.5, currentY + 8.2);
      colX += colPosW;

      // Col 4: Progress Lulus
      const progCenterX = colX + colProgW / 2;
      const isPerfect = person.passedCount === modules.length;
      const isInProgress = person.passedCount > 0 && !isPerfect;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      if (isPerfect) {
        doc.setTextColor(4, 120, 87);
        doc.text(`Lulus Sempurna (${person.passedCount}/${modules.length})`, progCenterX, currentY + 3.6, { align: 'center' });
      } else if (isInProgress) {
        doc.setTextColor(180, 83, 9);
        doc.text(`Dalam Proses (${person.passedCount}/${modules.length})`, progCenterX, currentY + 3.6, { align: 'center' });
      } else {
        doc.setTextColor(220, 38, 38);
        doc.text(`Belum Lulus (0/${modules.length})`, progCenterX, currentY + 3.6, { align: 'center' });
      }

      // Progress bar background
      const barW = 32;
      const barH = 1.6;
      const barX = progCenterX - barW / 2;
      const barY = currentY + 5.2;

      doc.setFillColor(226, 232, 240);
      doc.roundedRect(barX, barY, barW, barH, 0.5, 0.5, 'F');

      const fillW = Math.max(1, (person.passedCount / modules.length) * barW);
      if (isPerfect) {
        doc.setFillColor(16, 185, 129); // emerald
      } else if (isInProgress) {
        doc.setFillColor(245, 158, 11); // amber
      } else {
        doc.setFillColor(239, 68, 68); // red
      }
      doc.roundedRect(barX, barY, fillW, barH, 0.5, 0.5, 'F');

      // Fail status note or percentage
      if (person.hasFailedRecord) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(5.2);
        doc.setTextColor(185, 28, 28);
        doc.text(`Tercatat Tidak Lulus (${person.failedCount} Modul)`, progCenterX, currentY + 9.4, { align: 'center' });
      } else {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(5.5);
        doc.setTextColor(100, 116, 139);
        doc.text(`${person.percentComplete}% Selesai`, progCenterX, currentY + 9.4, { align: 'center' });
      }
      colX += colProgW;

      // Col 5-11: 7 Module Badges
      let modX = colX;
      modules.forEach((mod, mIdx) => {
        const mw = getModWidth(mIdx);
        const rec = person.modulesMap[mod.id];
        const isPassed = rec?.passed;
        const isAttempted = !!rec;
        const score = rec?.score;

        // Badge Box
        const bW = mw - 3;
        const bH = 7.6;
        const bX = modX + 1.5;
        const bY = currentY + 1.6;
        const bCenterX = bX + bW / 2;

        if (isPassed) {
          doc.setFillColor(236, 253, 245);
          doc.setDrawColor(110, 231, 183);
          doc.setLineWidth(0.2);
          doc.roundedRect(bX, bY, bW, bH, 0.8, 0.8, 'FD');

          // Module badge code
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(4.8);
          doc.setTextColor(6, 95, 70);
          doc.text(mod.badge, bCenterX, bY + 2.5, { align: 'center' });

          // Score and checkmark
          drawVectorCheckmark(bCenterX - 3.8, bY + 5.3, [4, 120, 87]);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(6.2);
          doc.setTextColor(4, 120, 87);
          doc.text(String(score), bCenterX + 1.2, bY + 6.0, { align: 'center' });
        } else if (isAttempted) {
          doc.setFillColor(254, 242, 242);
          doc.setDrawColor(252, 165, 165);
          doc.setLineWidth(0.2);
          doc.roundedRect(bX, bY, bW, bH, 0.8, 0.8, 'FD');

          // Module badge code
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(4.8);
          doc.setTextColor(153, 27, 27);
          doc.text(mod.badge, bCenterX, bY + 2.5, { align: 'center' });

          // Score and cross
          drawVectorCross(bCenterX - 3.8, bY + 5.3, [185, 28, 28]);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(6.2);
          doc.setTextColor(185, 28, 28);
          doc.text(String(score), bCenterX + 1.2, bY + 6.0, { align: 'center' });
        } else {
          doc.setFillColor(241, 245, 249);
          doc.setDrawColor(226, 232, 240);
          doc.setLineWidth(0.2);
          doc.roundedRect(bX, bY, bW, bH, 0.8, 0.8, 'FD');

          // Module badge code
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(4.8);
          doc.setTextColor(148, 163, 184);
          doc.text(mod.badge, bCenterX, bY + 2.5, { align: 'center' });

          // Dash for unattempted
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(6.5);
          doc.setTextColor(148, 163, 184);
          doc.text('-', bCenterX, bY + 6.0, { align: 'center' });
        }

        modX += mw;
      });

      currentY += rowHeight;
    });
  }

  // Draw Footer across all pages
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);

    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.25);
    doc.line(marginX, 203, marginX + contentWidth, 203);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.2);
    doc.setTextColor(148, 163, 184);
    doc.text('Dokumen Resmi Keselamatan Kerja PT United Tractors Tbk  •  Matriks 7 Safety Mandatory', marginX + 2, 206.5);
    doc.text('Aplikasi By IRL92', pageWidth / 2, 206.5, { align: 'center' });

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text(`Halaman ${p} dari ${totalPages}`, marginX + contentWidth - 2, 206.5, { align: 'right' });
  }

  // File name
  const areaSlug = areaLabel.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
  const dateSlug = dateNow.toISOString().slice(0, 10);
  doc.save(`Matriks_Pencapaian_7_Safety_Mandatory_${areaSlug}_${dateSlug}.pdf`);
};
